import React, { createContext, useContext, useState, useEffect } from 'react';
import { api, User } from '../services/api';

// Assuming 'User' type is defined elsewhere or will be defined.
// For example: interface User { id: string; email: string; /* ... other user properties */ }


interface AuthContextType {
    user: User | null;
    token: string | null;
    isAuthenticated: boolean;
    loading: boolean; // Added loading state
    activeAuthModal: 'login' | 'register' | 'admin-login' | null;
    openAuthModal: (mode?: 'login' | 'register' | 'admin-login') => void;
    closeAuthModal: () => void;
    login: (token: string, user: User) => void;
    logout: () => void;
    setUser: (user: User | null) => void;
}

const AuthContext = createContext<AuthContextType>({
    user: null,
    token: null,
    isAuthenticated: false,
    loading: true, // Default to true
    activeAuthModal: null,
    openAuthModal: () => { },
    closeAuthModal: () => { },
    login: () => { },
    logout: () => { },
    setUser: () => { }
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true); // Track initial load

    const [activeAuthModal, setActiveAuthModal] = useState<'login' | 'register' | 'admin-login' | null>(null);

    useEffect(() => {
        // Au chargement, si on a un token et pas de user, on le récupère
        if (token && !user) {
            // Check if it's a legacy token (Raw ID doesn't have dots)
            const isLegacy = !token.includes('.');

            if (isLegacy) {
                console.log("Legacy token detected, upgrading...");
                api.auth.refreshToken()
                    .then((data: any) => {
                        console.log("Token upgraded to JWT");
                        login(data.token, data.user);
                        setLoading(false);
                    })
                    .catch((err: any) => {
                        console.error("Failed to upgrade legacy token:", err);
                        logout();
                        setLoading(false);
                    });
            } else {
                api.users.getMe()
                    .then((userData: any) => {
                        console.log("Auth restored for:", userData.email);
                        setUser(userData);
                        // Sync Language
                        if (userData.language) {
                            import('../i18n/config').then(({ default: i18n }) => {
                                if (i18n.language !== userData.language) {
                                    i18n.changeLanguage(userData.language);
                                }
                            });
                        }
                    })
                    .catch((err: any) => {
                        console.error("Failed to restore session:", err);
                        logout();
                    })
                    .finally(() => {
                        setLoading(false); // Done loading
                    });
            }
        } else {
            // If no token, or user already there, we are done loading
            setLoading(false);
        }
    }, [token]);

    const login = (newToken: string, newUser: any) => {
        localStorage.setItem('token', newToken);
        setToken(newToken);
        setUser(newUser);

        // Sync Language on Login
        if (newUser.language) {
            import('../i18n/config').then(({ default: i18n }) => {
                i18n.changeLanguage(newUser.language);
            });
        }
    };

    const logout = () => {
        localStorage.removeItem('token');
        setToken(null);
        setUser(null);
    };

    const openAuthModal = (mode: 'login' | 'register' | 'admin-login' = 'login') => {
        setActiveAuthModal(mode);
    };

    const closeAuthModal = () => {
        setActiveAuthModal(null);
    };

    return (
        <AuthContext.Provider value={{
            user,
            token,
            login,
            logout,
            setUser,
            isAuthenticated: !!token,
            loading,
            activeAuthModal,
            openAuthModal,
            closeAuthModal
        }}>
            {children}
        </AuthContext.Provider>
    );
};
